import React from "react";
import { getLanguageFlag } from "./flags";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const clamp01 = (x) => clamp(x, 0, 1);

const formatMMSS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = String(Math.floor(s / 60));
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

export default function ProgressPill({
  doneSeconds = 0,
  goalSeconds,
  onClick,
  className = "",
  collapsed = false,
  minGoalSeconds = 1 * 60,
  language = "en",
  showLanguageFlag = false,
}) {
  // Calculations
  const rawGoal = Number(goalSeconds ?? minGoalSeconds);
  const safeGoal = Number.isFinite(rawGoal) ? Math.max(1, rawGoal) : minGoalSeconds;
  const rawDone = Number(doneSeconds);
  const safeDone = Number.isFinite(rawDone) ? Math.max(0, rawDone) : 0;

  const ratio = safeDone / safeGoal;
  const pctLabel = Math.round(ratio * 100);
  
  // ACCURATE visual width - 1% shows as 1%, 10% shows as 10%
  const pctBar = Math.round(clamp01(ratio) * 100);

  // Get language flag if needed
  const languageFlag = showLanguageFlag ? getLanguageFlag(language) : null;
  
  const timeText = `${formatMMSS(safeDone)} / ${formatMMSS(safeGoal)}`;
  const pctText = `${pctLabel}%`;
  const fullText = `${timeText} • ${pctText}`;
  const title = `Daily progress: ${fullText}`;
  const isClickable = typeof onClick === "function";

  const baseBtn =
  "text-left rounded-xl transition-all duration-300 font-medium text-xs sm:text-sm flex flex-col items-stretch justify-between group border bg-white/60 text-gray-600 hover:bg-white/80 border-white/40 hover:border-white/60 hover:shadow-md";
  const selectedIcon = "🏆";

  return (
    <button
      type="button"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      title={title}
      className={[
        baseBtn,
        !isClickable ? "opacity-60 cursor-default hover:shadow-none" : "",
        "py-3 px-3 sm:py-4 sm:px-4 w-full", // Always use expanded layout
        className,
      ].join(" ")}
    >
      <div className="w-full space-y-2 sm:space-y-3">
        {/* Top row: Label + Flag */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm bg-gray-100 group-hover:bg-gray-200 transition-all duration-300">
              {selectedIcon}
            </div>
            
            <div className="font-medium text-gray-800 flex items-center gap-2">
              Daily Progress
              {/* Language flag next to text - VISIBLE ON MOBILE */}
              {showLanguageFlag && languageFlag && (
                <span className="text-xs font-normal opacity-75">
                  {languageFlag}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Middle: Progress Bar with ACCURATE width */}
        <div className="w-full">
<div className="h-2 sm:h-3 w-full overflow-hidden rounded-full bg-gray-200 relative">
            <div
              key={`progress-${pctBar}`}
              style={{
                height: '100%',
                width: `${pctBar}%`,
                background: 'linear-gradient(to right, #34d399, #10b981)',
                borderRadius: '9999px',
                position: 'absolute',
                left: 0,
                top: 0,
                transition: 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.3)',
              }}
            />
            
            {/* Shine effect */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '30%',
                width: `${pctBar}%`,
                background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent)',
                borderRadius: '9999px',
                pointerEvents: 'none',
                transition: 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>
        </div>

        {/* Bottom: Time (MM:SS) and Percentage */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {timeText}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-gray-700">
              {pctText}
            </div>
            <div className={`w-2 h-2 rounded-full ${pctBar > 0 ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          </div>
        </div>
      </div>
    </button>
  );
}